import variables from 'variables/variables'
import { notNull } from '@gap/utils/utils'
import type Chargebee from '@chargebee/chargebee-js-types'

/** A deep-linkable Chargebee portal section we open directly (matches Chargebee's getPortalSections() keys). */
export type ChargebeePortalSection = 'BILLING_HISTORY' | 'EDIT_SUBSCRIPTION' | 'SUBSCRIPTION_DETAILS'

// Types come from @chargebee/chargebee-js-types; window.Chargebee is its global (typed `any`, cast via chargebeeGlobal).
type ChargebeeInstance = ReturnType<Chargebee['init']>

/** The Chargebee.js global, typed (the package declares `window.Chargebee` as `any`). Call only after loadScript(). */
const chargebeeGlobal = (): Chargebee => notNull(window.Chargebee)

const CHARGEBEE_SCRIPT_SRC = 'https://js.chargebee.com/v2/chargebee.js'

let scriptPromise: Promise<void> | null = null
let instance: ChargebeeInstance | null = null

// Chargebee reuses one #cb-frame iframe and paints the PREVIOUS plan until the new page renders. Mask that by
// forcing Chargebee's own #cb-loader visible while <body> has CHECKOUT_LOADING_CLASS. Style-only (never touch its nodes).
const CHECKOUT_LOADING_CLASS = 'cb-checkout-loading'
let checkoutLoadingStylesheetInjected = false
let checkoutLoadingToken = 0

const ensureCheckoutLoadingStylesheet = (): void => {
   if (checkoutLoadingStylesheetInjected) {
      return
   }
   const style = document.createElement('style')
   style.dataset.source = 'chargebee-checkout-loading-mask'
   style.textContent = `body.${CHECKOUT_LOADING_CLASS} #cb-loader { visibility: visible !important; z-index: 1000000 !important; }`
   document.head.appendChild(style)
   checkoutLoadingStylesheetInjected = true
}

const loadScript = (): Promise<void> => {
   if (window.Chargebee) {
      return Promise.resolve()
   }
   if (!scriptPromise) {
      scriptPromise = new Promise<void>((resolve, reject) => {
         const script = document.createElement('script')
         script.src = CHARGEBEE_SCRIPT_SRC
         script.async = true
         script.onload = () => resolve()
         script.onerror = () => {
            // Drop the cached rejection (and the dead tag) so the next call retries. preloadChargebee() loads on page
            // mount and swallows the error, so keeping it would break every later portal/checkout until a reload.
            scriptPromise = null
            script.remove()
            reject(new Error('Failed to load Chargebee.js'))
         }
         document.head.appendChild(script)
      })
   }
   return scriptPromise
}

const getChargebeeInstance = async (): Promise<ChargebeeInstance> => {
   await loadScript()
   if (!instance) {
      instance = chargebeeGlobal().init({
         site: variables.chargebeeSite,
         publishableKey: variables.chargebeePublishableKey,
      })
   }
   return instance
}

let preconnected = false

/** The Chargebee admin page for a customer (customer id = org id) on this environment's site. */
export const chargebeeCustomerAdminUrl = (orgId: string): string => `https://${variables.chargebeeSite}.chargebee.com/d/customers/${orgId}`

/**
 * Warms Chargebee on billing-page mount so the first portal/checkout open is snappy(er), still slow but better.
 */
export const preloadChargebee = (): void => {
   if (!preconnected) {
      preconnected = true
      const hosts = [
         'https://js.chargebee.com',
         'https://js1.chargebeestatic.com',
         ...(variables.chargebeeSite ? [`https://${variables.chargebeeSite}.chargebee.com`] : []),
      ]
      for (const host of hosts) {
         const preconnect = document.createElement('link')
         preconnect.rel = 'preconnect'
         preconnect.href = host
         preconnect.crossOrigin = 'anonymous'
         document.head.appendChild(preconnect)
      }
   }
   void getChargebeeInstance().catch(() => undefined)
}

/**
 * Opens Chargebee's hosted self-serve portal modal. `createSession` must resolve
 * to a Chargebee portal session object (created server-side for the org's customer).
 */
export const openChargebeePortal = async (params: { createSession: () => Promise<unknown>; onClose?: () => void; section?: ChargebeePortalSection; subscriptionId?: string }): Promise<void> => {
   const chargebee = await getChargebeeInstance()
   // Close any open modal first (no-op if none). Never remove Chargebee's modal DOM — it caches #cb-container/its iframe.
   chargebee.closeAll()
   // Chargebee caches the resolved portal session across opens, so an expired one resurfaces as "session expired".
   // logout() clears it (optional-chained: a build without it degrades gracefully).
   chargebee.logout?.()
   // Our PortalSession has `token`; cast to the provider's { token: string } shape.
   chargebee.setPortalSession(params.createSession as () => Promise<{ token: string }>)
   const portal = chargebee.createChargebeePortal()
   const callbacks = params.onClose ? { close: params.onClose } : {}
   // Deep-link to a section via getPortalSections() (on the global, not the instance); the subscription sections also
   // need the subscription id. Otherwise open the portal home.
   if (params.section) {
      // Cast the options: getPortalSections() returns an enum whose values satisfy the portal's raw-string sectionType.
      portal.open(callbacks, {
         sectionType: chargebeeGlobal().getPortalSections()[params.section],
         ...(params.subscriptionId ? { params: { subscriptionId: params.subscriptionId } } : {}),
      } as Parameters<typeof portal.open>[1])
   } else {
      portal.open(callbacks)
   }
}

// Opens Chargebee's In-App Checkout modal. 
export const openChargebeeCheckout = async (params: { createHostedPage: () => Promise<unknown>; onSuccess?: () => void }): Promise<void> => {
   const chargebee = await getChargebeeInstance()
   ensureCheckoutLoadingStylesheet()
   chargebee.closeAll()

   // Mask the reused iframe's stale (previous-plan) content with Chargebee's loader until the new page
   // loads. The token guards against an earlier open's callback/timeout unmasking a later open.
   const loadingToken = (checkoutLoadingToken += 1)
   document.body.classList.add(CHECKOUT_LOADING_CLASS)
   const stopMasking = (): void => {
      if (loadingToken === checkoutLoadingToken) {
         document.body.classList.remove(CHECKOUT_LOADING_CLASS)
      }
   }
   // Fallback so the mask can never get stuck if no terminal callback fires.
   setTimeout(stopMasking, 15000)

   chargebee.openCheckout({
      hostedPage: params.createHostedPage as () => Promise<{ url: string }>,
      layout: 'in_app',
      loaded: stopMasking,
      success: params.onSuccess,
      close: stopMasking,
      error: stopMasking,
   })
}

/** Closes any open Chargebee.js modal. Used after a successful checkout to dismiss the confirmation screen. */
export const closeChargebeeModals = (): void => {
   instance?.closeAll()
}
